import _ from "lodash";
import moment from "moment";
import { getFullName as getPersonalRecordExerciseKey } from "./personal-records";

export { getPersonalRecordExerciseKey };
import convert from "convert-units";
import {
  DATE_FORMATS,
  OUTCOME_STATUS,
  MEASUREMENT_SYSTEM,
  PR_TYPES,
} from "../constants";

/**
 *
 * @param {Oject} exercise to check
 * @returns true if at least one set of the exercise has outcomes property,
 * false otherwise
 */
const hasOutcomes = (exercise = {}) => {
  if (!exercise.sets) {
    return false;
  }
  return exercise.sets.some((set) => !!set.outcomes);
};

/**
 * @param {Array} exercises arbitrary list of historic exercises
 * that can span across multiple days
 * @param {Number} days how long back to filter exercises
 * ex: days = 10, means it will only keep exercises from the last 10 days
 *
 * Keeps elements that have primaryMuscleGroup,
 * Keeps elements that have outcome
 * @returns the filtered exercises
 */
const filterAllExercises = (
  exercises = [],
  days = 90,
  currentDate,
  isComposition
) => {
  const today = moment(currentDate, DATE_FORMATS.DATE_DAY_FORMAT);
  const todayUtc = parseInt(today.format(DATE_FORMATS.UTC));
  const pastDate = parseInt(
    today.clone().subtract(days, "days").format(DATE_FORMATS.UTC)
  );
  const filterByDate = (exer) =>
    exer.utcDate <= todayUtc &&
    exer.utcDate >= pastDate &&
    !!exer.primaryMuscleGroup &&
    hasOutcomes(exer);

  const filterByCondions = (exer) =>
    !!exer.primaryMuscleGroup && hasOutcomes(exer);
  const filteredExercises = exercises.filter(
    isComposition ? filterByCondions : filterByDate
  );
  return filteredExercises;
};

/**
 * @param {Array} exercises to group into days
 * The function assumes the exercises are sorted by utcDate otherwise
 * it might not work as expected
 * Doesn't matters if the sorting is ascendant or descendant
 * @returns an array of days, the elements are of the form
 * {utcDate: 20211031, exercises [...]}
 */
export const groupExercisesByDay = (exercises = []) => {
  if (exercises.length === 0) return;

  let i = 1;
  const days = [{ utcDate: exercises[0].utcDate, exercises: [exercises[0]] }];
  while (i < exercises.length) {
    // check if exercise is from the last day's element date
    if (days[days.length - 1].utcDate === exercises[i].utcDate) {
      // if so, add it to that day
      days[days.length - 1].exercises.push(exercises[i]);
    } else {
      // add to a new day
      days.push({ utcDate: exercises[i].utcDate, exercises: [exercises[i]] });
    }
    i++;
  }
  return days;
};

/**
 *
 * @param {Object} set of an exercise
 * @returns the total outcome sets for that exercise
 */
export const getOutcomeSets = (set = {}) => {
  if (!set) {
    return 0;
  }
  if (!set.outcomes) {
    return 0;
  }
  const filteredSets = Object.keys(set.outcomes).filter(
    (key) =>
      set.outcomes[key] && set.outcomes[key].status === OUTCOME_STATUS.COMPLETED
  ).length; // the amount of outcomes completed equals the sets
  return filteredSets;
};

/**
 * Folds the selectable/legacy "Legs" set counts into the Quadriceps and
 * Hamstrings regions (additively), then drops the "Legs" key. "Legs" always
 * represents both leg regions, so its sets must be summed into each.
 * Mutates and returns the same object.
 * @param {Object} muscleGroups sets keyed by muscle group
 * @returns {Object} the object with "Legs" merged into Quadriceps/Hamstrings
 */
const foldLegsIntoLegRegions = (muscleGroups) => {
  if (muscleGroups.Legs) {
    muscleGroups.Quadriceps =
      (muscleGroups.Quadriceps || 0) + muscleGroups.Legs;
    muscleGroups.Hamstrings =
      (muscleGroups.Hamstrings || 0) + muscleGroups.Legs;
    delete muscleGroups.Legs;
  }
  return muscleGroups;
};

const calculateMaxSetsPerMuscleGroup = (exerciseDays) => {
  if (!exerciseDays) return {};
  const maxMuscleGroups = {};
  exerciseDays.forEach((day) => {
    const muscleGroups = {};
    day.exercises.forEach((exercise) => {
      const { primaryMuscleGroup } = exercise;
      const { secondaryMuscleGroup = "" } = exercise;
      if (!exercise.sets) {
        return;
      }
      // calculating the total sets for each muscle group of one exercise
      exercise.sets.forEach((set) => {
        const sets = getOutcomeSets(set);

        if (primaryMuscleGroup in muscleGroups) {
          muscleGroups[primaryMuscleGroup] += sets;
        } else {
          calculateMaxSetsPerMuscleGroup;
          muscleGroups[primaryMuscleGroup] = sets;
        }

        if (!secondaryMuscleGroup) {
          return;
        }

        if (secondaryMuscleGroup in muscleGroups) {
          muscleGroups[secondaryMuscleGroup] += Math.round(sets / 2);
        } else {
          muscleGroups[secondaryMuscleGroup] = Math.round(sets / 2);
        }
      });
    });
    // at this point is had the total sets for each muscle group in a day
    foldLegsIntoLegRegions(muscleGroups);
    Object.keys(muscleGroups).forEach((key) => {
      // store the max value if found for each muscle group
      if (!(key in maxMuscleGroups)) {
        // if no muscleGroup added, then add it
        maxMuscleGroups[key] = muscleGroups[key];
      } else {
        if (muscleGroups[key] > maxMuscleGroups[key]) {
          maxMuscleGroups[key] = muscleGroups[key];
        }
      }
    });
  });
  return maxMuscleGroups;
};

/**
 * @param {Array} exercises
 * Intended to filter exercises of a day single day
 * Keeps exercises that have outcomes
 * Keeps exercises that have primaryMuscleGroups
 * @returns the filtered exercises
 */
const filterDayExercises = (exercises = []) => {
  const filteredExercises = exercises.filter(
    (exer) => !!exer.primaryMuscleGroup && hasOutcomes(exer)
  );
  return filteredExercises;
};

/**
 *
 * @param {Array} dayExercises the array of exercises of a given day
 * Intended to calculate the total sets for each muscle group in one session
 * @returns an object with the muscleGroup as props and as values total amount of sets
 * for that muscle group in that day
 */
const calculateSetsPerMuscleGroup = (dayExercises) => {
  if (!dayExercises || dayExercises.length === 0) return {};
  const muscleGroups = {};
  dayExercises.forEach((exercise) => {
    const { primaryMuscleGroup } = exercise;
    const { secondaryMuscleGroup = "" } = exercise;
    // calculating the total sets for each muscle group of one exercise
    exercise.sets.forEach((set) => {
      let sets = 0;
      if (set && set.outcomes) {
        sets = Object.keys(set.outcomes).filter(
          (key) =>
            set.outcomes[key] &&
            set.outcomes[key].status === OUTCOME_STATUS.COMPLETED
        ).length; // the amount of outcomes completed equals the sets
      }

      if (primaryMuscleGroup in muscleGroups) {
        muscleGroups[primaryMuscleGroup] += sets;
      } else {
        muscleGroups[primaryMuscleGroup] = sets;
      }

      if (!secondaryMuscleGroup) {
        return;
      }

      if (secondaryMuscleGroup in muscleGroups) {
        muscleGroups[secondaryMuscleGroup] += Math.round(sets / 2);
      } else {
        muscleGroups[secondaryMuscleGroup] = Math.round(sets / 2);
      }
    });
  });
  return foldLegsIntoLegRegions(muscleGroups);
};

/**
 *
 * @param {Object} maxMuscleGroups maximum sets per muscle group found in a
 * particular date range
 * @param {Object} dayMuscleGroups sets per muscle groups found in a day
 * @returns an object with the percentage of sets in maxMaxMuscleGroups that
 * dayMuscleGroups represents.
 *
 * only muscle groups present in dayMuscleGroups are taken into account
 */
export const calculateDayMuscleGroupsIntensity = (
  maxMuscleGroups = {},
  dayMuscleGroups = {}
) => {
  const muscleGroupsIntensity = {};
  Object.keys(dayMuscleGroups).forEach((key) => {
    if (key in maxMuscleGroups) {
      muscleGroupsIntensity[key] =
        Math.round((dayMuscleGroups[key] / maxMuscleGroups[key]) * 10000) / 100;
    }
  });
  return muscleGroupsIntensity;
};

const selectTodayExercises = (locals) => locals.activeExercises || [];

export const readyData = ({
  date,
  exerciseList,
  activeExercises,
  isComposition,
}) => {
  const filteredExercises = filterAllExercises(
    _.cloneDeep(exerciseList),
    90,
    date,
    isComposition
  );

  const exerciseDays = groupExercisesByDay(filteredExercises);
  const maxSetPerMuscleGroup = calculateMaxSetsPerMuscleGroup(exerciseDays);
  const currentDateExercises = filterDayExercises(activeExercises);

  const currentDateMuscleGroups =
    calculateSetsPerMuscleGroup(currentDateExercises);

  const muscleGroupsIntensity = calculateDayMuscleGroupsIntensity(
    maxSetPerMuscleGroup,
    currentDateMuscleGroups
  );

  return muscleGroupsIntensity;
};

/**
 *
 * @param {Float} intensity number in percentage representing the volume peak for an exercise
 * @returns {String} color that represents the intensity for that color
 */
const calculateIntensityColor = (intensity) => {
  let color = "#fff";
  if (intensity >= 1 && intensity < 25) {
    color = `#FCCED6`;
  }
  if (intensity >= 25 && intensity < 50) {
    color = `#F89DAD`;
  }
  if (intensity >= 50 && intensity < 70) {
    color = `#F46C85`;
  }
  if (intensity >= 70 && intensity < 85) {
    color = `#F03B5C`;
  }
  if (intensity >= 85 && intensity < 100) {
    color = `#ED0A34`;
  }
  if (intensity >= 100) {
    color = "rgba(255,0,0, 1)";
  }
  return color;
};

/**
 *
 * @param {Object} muscleGroupsIntensity that contains the intensity
 * in percentage per each muscle group
 * @returns the intensity color per each of the muscle groups in
 * muscleGroupsIntensity in an object
 */
export const calculateMuscleGroupsColors = (muscleGroupsIntensity) => {
  const muscleGroupsColors = {};
  Object.keys(muscleGroupsIntensity).forEach((key) => {
    muscleGroupsColors[key] = calculateIntensityColor(
      muscleGroupsIntensity[key]
    );
  });
  return muscleGroupsColors;
};

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 *
 * @param {Object} obj
 * @param {string} field
 * @param {"imperial" | "metric"} measurementSystem
 * @returns Obj[field] rounded to 1 decimal places and
 * converted to imperial if measurement system is imperial
 */
const formatAndConvertWeight = (value, measurementSystem) => {
  const result =
    measurementSystem === MEASUREMENT_SYSTEM.IMPERIAL
      ? _.round(convert(value).from("kg").to("lb"), 1)
      : _.round(value, 1);
  return numberWithCommas(result);
};

/**
 *
 * @param {} recordsObject
 * Modifies recordsObject
 * Converts the record weights to Imperial
 * Shows the weights separated with commas like 1,000
 */
export const convertAndFormatRecordWeights = (
  recordsObject,
  measurementSystem
) => {
  const obj = _.cloneDeep(recordsObject);
  const RECORDS_CONVERTER = {
    repMaxPR: (repMaxObj) => {
      for (const exerciseName in repMaxObj) {
        for (const rep in repMaxObj[exerciseName]) {
          if (repMaxObj[exerciseName][rep].load) {
            repMaxObj[exerciseName][rep].load = formatAndConvertWeight(
              repMaxObj[exerciseName][rep].load,
              measurementSystem
            );
          }
          if (repMaxObj[exerciseName][rep].delta) {
            repMaxObj[exerciseName][rep].delta = formatAndConvertWeight(
              repMaxObj[exerciseName][rep].delta,
              measurementSystem
            );
          }
        }
      }
      return repMaxObj;
    },
    volumePR: (volumePRObj) => {
      for (const exerciseName in volumePRObj) {
        if (volumePRObj[exerciseName].volume) {
          volumePRObj[exerciseName].volume = formatAndConvertWeight(
            volumePRObj[exerciseName].volume,
            measurementSystem
          );
        }
        if (volumePRObj[exerciseName].delta) {
          volumePRObj[exerciseName].delta = formatAndConvertWeight(
            volumePRObj[exerciseName].delta,
            measurementSystem
          );
        }
      }
      return volumePRObj;
    },
    e1rmPR: (e1rmPRObj) => {
      for (const exerciseName in e1rmPRObj) {
        if (e1rmPRObj[exerciseName].e1rm) {
          e1rmPRObj[exerciseName].e1rm = formatAndConvertWeight(
            e1rmPRObj[exerciseName].e1rm,
            measurementSystem
          );
        }
        if (e1rmPRObj[exerciseName].delta) {
          e1rmPRObj[exerciseName].delta = formatAndConvertWeight(
            e1rmPRObj[exerciseName].delta,
            measurementSystem
          );
        }
      }
      return e1rmPRObj;
    },
  };
  for (const recordName in obj) {
    if (recordName in RECORDS_CONVERTER) {
      obj[recordName] = RECORDS_CONVERTER[recordName](obj[recordName]);
    }
  }
  return obj;
};

export const isPRDataEmpty = (PRData = {}) => {
  if (_.isEmpty(PRData)) {
    return true;
  }

  for (const prType of PR_TYPES) {
    if (prType.name in PRData && !_.isEmpty(PRData[prType.name])) {
      return false;
    }
  }
  return true;
};

const prDateToDayKey = (date) => {
  if (!date) return null;
  return moment.utc(date).format("YYYY-MM-DD");
};

const addPrKeyForDay = (byDay, dayKey, exerciseKey) => {
  if (!dayKey || !exerciseKey) return;
  if (!byDay[dayKey]) byDay[dayKey] = new Set();
  byDay[dayKey].add(exerciseKey);
};

/**
 * Index personal-records API data by calendar day for Log view PR indicators.
 * @param {Object} prData - { repMaxPR, volumePR, e1rmPR } from personal-records endpoint
 * @returns {Record<string, Set<string>>} dayKey -> exercise keys (getFullName format)
 */
export const buildPrExerciseKeysByDay = (prData = {}) => {
  const byDay = {};
  const { repMaxPR = {}, volumePR = {}, e1rmPR = {} } = prData;

  for (const [exerciseKey, repsMap] of Object.entries(repMaxPR)) {
    for (const info of Object.values(repsMap || {})) {
      addPrKeyForDay(byDay, prDateToDayKey(info?.date), exerciseKey);
    }
  }

  for (const [exerciseKey, info] of Object.entries(volumePR)) {
    addPrKeyForDay(byDay, prDateToDayKey(info?.date), exerciseKey);
  }

  for (const [exerciseKey, info] of Object.entries(e1rmPR)) {
    addPrKeyForDay(byDay, prDateToDayKey(info?.date), exerciseKey);
  }

  return byDay;
};

export const getWorkloadDistributionData = (exercises) => {
  const workloadDistribution = { Hypertrophy: 0, Strength: 0, Power: 0 };

  exercises.forEach((exer) => {
    if (!exer.sets) return;
    exer.sets.forEach((set) => {
      if (!set.outcomes) return;
      const sets = getOutcomeSets(set);
      const reps = set.reps || 0;
      if (reps > 0 && reps < 4) {
        workloadDistribution.Power += sets;
      } else if (reps > 3 && reps < 7) {
        workloadDistribution.Strength += sets;
      } else if (reps > 6) {
        workloadDistribution.Hypertrophy += sets;
      }
    });
  });

  Object.keys(workloadDistribution).forEach((key) => {
    if (workloadDistribution[key] === 0) {
      delete workloadDistribution[key];
    }
  });

  return [
    Object.keys(workloadDistribution),
    Object.values(workloadDistribution),
  ];
};

/**
 *
 * @param {Array} exercises must be exercise.day array from the redux store (i.e: the state) *
 * @returns returns the exercises array with the load and e1rm parameter in the outcomes converted to lbs
 * Does not modify the exercises array
 */
export const convertOutcomeE1rmAndLoads = (exercises) => {
  if (!exercises) return [];
  const exercisesCopy = _.cloneDeep(exercises);
  // modify the exercisesCopy outcome loads and e1rm
  exercisesCopy.forEach((exer) => {
    exer.sets.forEach((set) => {
      if (!set.outcomes) return;
      const outcomes = set.outcomes;

      Object.keys(outcomes).forEach((key) => {
        const outcome = outcomes[key];
        if (outcome?.outcome?.load) {
          const load = _.round(
            convert(outcome.outcome.load).from("kg").to("lb"),
            1
          );
          // const load = convertLoadFn(outcome.outcome.load);
          outcome.outcome.load = load;
        }
        if (outcome?.outcome?.e1rm) {
          const e1rm = _.round(
            convert(outcome.outcome.e1rm).from("kg").to("lb"),
            1
          );
          outcome.outcome.e1rm = e1rm;
        }
      });
    });
  });
  return exercisesCopy;
};

export const filterExercisesByDateRange = ({
  exercises = [],
  startUtc,
  endUtc,
}) => {
  return exercises.filter(
    (elem) => elem.utcDate >= startUtc && elem.utcDate <= endUtc
  );
};

export const getVolumePerDay = ({ exercisesDays }) => {
  if (!exercisesDays || exercisesDays.length === 0) return {};
  const volumeDays = [];
  exercisesDays.forEach((exerciseDay) => {
    let volume = 0;
    exerciseDay.exercises.forEach((exercise) => {
      exercise.sets.forEach((set) => {
        if (!set || _.isEmpty(set.outcomes)) {
          return;
        }
        Object.keys(set.outcomes).forEach((key) => {
          if (set.outcomes[key].status !== OUTCOME_STATUS.COMPLETED) {
            return;
          }

          const outcome = set.outcomes[key].outcome;
          if (!outcome) {
            return;
          }

          if (
            !Number.isFinite(outcome.load) ||
            !Number.isFinite(outcome.reps)
          ) {
            return;
          }

          volume += _.round(outcome.load * outcome.reps);
        });
      });
    });
    volumeDays.push({ volume, utcDate: exerciseDay.utcDate });
  });
  return volumeDays;
};
